import { useId, type ReactNode } from 'react';

export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

/**
 * Champ de formulaire accessible : libellé relié au contrôle, aide et erreur reliées par `aria-describedby`.
 * Le contrôle reçoit ses attributs par rendu : `<Field label="…">{(p) => <input {...p} />}</Field>`.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (props: FieldControlProps) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const props: FieldControlProps = { id };
  if (describedBy) props['aria-describedby'] = describedBy;
  if (error) props['aria-invalid'] = true;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(props)}
      {hint && (
        <span id={hintId} className="field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="field-error-inline" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
